import { isMatchFinishedStatus } from "../../Trader/Cricket/decimalEventStatus";
import { extractCompetition } from "../../Games/competitionUtils";
import {
    cricketStreamIndicatesInningsBreak,
    getCricketStatusFromStream,
    getMatchStatus,
    isCricketBreakInPlayMatchStatus,
    isStreamingFixtureLive,
    parseCricketMatchName,
} from "../../Games/matchUtils";
import type { Match } from "../../Games/MatchCards/types";
import { cricketFormatOversFromText } from "../../Cricket/cricketMatchCardOverDisplay";
import { normalizeCricketFormatCode } from "../../../utils/cricketTestMatch";
import { getAuthHeaders } from "../../../utils/authHeaders";
import {
    parseCompletedCricketArchive,
    type LeagueCompletedArchive,
} from "./leagueCompletedArchive";
import type { LeagueFixture } from "./types";

const CACHE_MS = 30_000;

type Cache = { at: number; matches: Match[] };

let cache: Cache | null = null;
let inflight: Promise<Match[]> | null = null;

export function isCricketMatchFinished(match: Match): boolean {
    const status = String(match.status ?? match.event_status ?? "").toUpperCase();
    return (
        status.includes("COMPLETE") ||
        status.includes("FINISHED") ||
        status.includes("CLOSED") ||
        status.includes("ABANDON") ||
        status === "10" ||
        status === "11" ||
        status === "13"
    );
}

/** True when the fixture has already started or the feed says it is live. */
export function isCricketMatchInPlay(match: Match): boolean {
    if (isCricketMatchFinished(match)) return false;
    const startMs = match.start ? new Date(match.start).getTime() : NaN;
    if (Number.isFinite(startMs) && startMs > Date.now()) return false;
    const list = getMatchStatus({ ...match, sport: match.sport || "cricket" });
    if (isCricketBreakInPlayMatchStatus(list)) return false;
    if (
        list.text === "IN-PLAY" ||
        list.text.toUpperCase().includes("SUPER OVER") ||
        list.text.toUpperCase().includes("BALL IN PROGRESS")
    ) {
        return true;
    }
    return isStreamingFixtureLive(String(match.status ?? ""));
}

/** Map a feed "Break in Play …" line onto the short labels used on league cards / pills. */
function leagueBreakLabelFromStatusText(text: string, streamData?: unknown): string {
    if (/innings/i.test(text) || (streamData != null && cricketStreamIndicatesInningsBreak(streamData))) {
        return "INNINGS BREAK";
    }
    if (/rain|bad weather|weather delay|toss delayed|wet outfield|covers/i.test(text)) {
        return "MATCH BREAK - RAIN DELAY";
    }
    if (/drinks/i.test(text)) return "DRINKS";
    if (/injur/i.test(text)) return "INJURY";
    if (/time\s*out|strategic/i.test(text)) return "TIME OUT";
    return "MATCH BREAK";
}

function streamScoreboardValue(streamData: unknown, name: string): string {
    if (!streamData || typeof streamData !== "object") return "";
    const data = streamData as {
        event?: { scoreboard?: unknown; event?: { scoreboard?: unknown } };
    };
    const board = data.event?.scoreboard ?? data.event?.event?.scoreboard;
    if (!Array.isArray(board)) return "";
    const entry = board.find((row) => (row as { name?: string })?.name === name) as
        | { value?: unknown }
        | undefined;
    const value = entry?.value;
    return value == null ? "" : String(value).trim();
}

/**
 * True when the live cricket stream says the match is over — result, event closed,
 * or abandoned / no result after rain. A rain delay that is still a break is not over.
 */
export function cricketStreamIndicatesMatchFinished(streamData: unknown): boolean {
    if (!streamData || typeof streamData !== "object") return false;
    const data = streamData as {
        market_update?: { event_status?: unknown };
        event?: { event_status?: unknown };
        scorecard?: { event_status?: unknown };
    };
    const candidates = [
        data.market_update?.event_status,
        data.event?.event_status,
        data.scorecard?.event_status,
        streamScoreboardValue(streamData, "game_phase"),
        streamScoreboardValue(streamData, "status"),
    ];
    if (candidates.some((raw) => raw != null && raw !== "" && isMatchFinishedStatus(raw))) return true;
    const winner = streamScoreboardValue(streamData, "winner").toUpperCase();
    return winner !== "" && winner !== "NA";
}

/** "INNINGS BREAK" / "MATCH BREAK" while the fixture feed reports a stoppage, else null. */
export function leagueFixtureBreakLabel(match: Match): string | null {
    if (isCricketMatchFinished(match)) return null;
    const statusId = Number((match as { status_id?: unknown }).status_id);
    const statusText = String(match.status ?? "");
    const onBreak =
        statusId === 7 ||
        isCricketBreakInPlayMatchStatus(getMatchStatus(match));
    if (!onBreak) return null;
    return leagueBreakLabelFromStatusText(statusText);
}

/**
 * Break label from the live cricket stream (scoreboard / event_status).
 * Catches the long innings-break window when Decimal has dropped back to MATCH_STABLE
 * and the events list still reads "Match in Progress".
 */
export function leagueBreakLabelFromStream(streamData: unknown): string | null {
    if (!streamData) return null;
    const status = getCricketStatusFromStream(streamData);
    if (!status || !isCricketBreakInPlayMatchStatus(status)) return null;
    return leagueBreakLabelFromStatusText(status.text, streamData);
}

function durationHoursForMatch(match: Match): number {
    const blob = `${match.format || ""} ${match.name || ""} ${match.competition || ""}`.toLowerCase();
    if (blob.includes("test")) return 8;
    if (blob.includes("odi") || blob.includes("50 over") || blob.includes("50-over")) return 8;
    return 4;
}

/** Limited-overs format code for seat sizing (10 / 20 / 50 / 100). */
export function formatOversForMatch(match: Match): number | null {
    const fromFields = normalizeCricketFormatCode(match.format);
    if (fromFields != null && fromFields !== 999) return fromFields;
    return cricketFormatOversFromText(match.competition, match.name);
}

export async function fetchLeagueCricketMatches(): Promise<Match[]> {
    if (cache && Date.now() - cache.at < CACHE_MS) return cache.matches;
    if (inflight) return inflight;

    inflight = (async () => {
        const api = process.env.API_URL;
        if (!api) throw new Error("API_URL is not set");
        const response = await fetch(`${api}/trade/cricket/events?maxmatches=80`, {
            method: "GET",
            headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error(`cricket events ${response.status}`);
        const payload = await response.json();
        const matches: Match[] = (Array.isArray(payload?.matches) ? payload.matches : []).map((row: Match) => ({
            ...row,
            sport: row.sport || "cricket",
        }));
        cache = { at: Date.now(), matches };
        return matches;
    })().finally(() => {
        inflight = null;
    });

    return inflight;
}

export function cricketMatchToLeagueFixture(match: Match): LeagueFixture | null {
    if (!match?.id || isCricketMatchFinished(match) || isCricketMatchInPlay(match)) return null;
    const { teams } = parseCricketMatchName(match.name || "");
    const participants = match["event-participants"] || [];
    const home = teams[0] || participants[0]?.name || participants[0]?.["participant-name"] || "";
    const away = teams[1] || participants[1]?.name || participants[1]?.["participant-name"] || "";
    if (!home || !away) return null;
    const startMs = match.start ? new Date(match.start).getTime() : NaN;
    return {
        id: String(match.id),
        name: `${home} v ${away}`,
        homeTeam: home,
        awayTeam: away,
        competition: extractCompetition(match, "CRICKET") || match.competition || "Cricket",
        start: Number.isFinite(startMs) ? new Date(startMs).toISOString() : new Date().toISOString(),
        durationHours: durationHoursForMatch(match),
        formatOvers: formatOversForMatch(match),
    };
}

export function leagueFixturesFromCricketMatches(matches: Match[]): LeagueFixture[] {
    const seen = new Set<string>();
    const fixtures: LeagueFixture[] = [];
    for (const match of matches) {
        const fixture = cricketMatchToLeagueFixture(match);
        if (!fixture || seen.has(fixture.id)) continue;
        seen.add(fixture.id);
        fixtures.push(fixture);
    }
    fixtures.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    return fixtures;
}

export function isPrototypeFixtureId(fixtureId: string | null | undefined): boolean {
    return Boolean(fixtureId?.startsWith("proto-"));
}

/**
 * Archived match centre payload for a finished fixture — same `/completed/:id` source as
 * Match details. Fills thin scorecards from the event scoreboard and derives the who-won line.
 */
export async function fetchCompletedCricketArchive(matchId: string): Promise<LeagueCompletedArchive | null> {
    const id = matchId.trim();
    if (!id || isPrototypeFixtureId(id)) return null;
    const api = process.env.API_URL;
    if (!api) return null;
    try {
        const response = await fetch(`${api}/completed/${id}`, {
            method: "GET",
            headers: getAuthHeaders(),
        });
        if (!response.ok) return null;
        const payload = await response.json();
        return parseCompletedCricketArchive(payload);
    } catch {
        return null;
    }
}

/** @deprecated Prefer `fetchCompletedCricketArchive` — kept for callers that only need inns scores. */
export async function fetchCompletedCricketScorecard(matchId: string): Promise<unknown | null> {
    const archive = await fetchCompletedCricketArchive(matchId);
    return archive?.scorecard ?? null;
}
